import { setConfig } from "barikoiapis";
import {
  FullscreenControl,
  GeolocateControl,
  Map,
  Marker,
  NavigationControl,
  Popup,
} from "bkoi-gl";
import { debounce } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import UserIcon from "../../../assets/icons/UserIcon";
import EmployeeInfoItem from "../../../components/common/EmployeeInfoItem";
import InfoTitle from "../../../components/common/InfoTitle";
import Loader from "../../../components/ui/Loader";
import TimelineItem from "../../../components/Visit/TimelineItem";
import colors from "../../../constants/colors";
import {
  BARIKOI_API_KEY,
  dateFormat,
  dbDateFormat,
  dbTimeFormat,
  timeFormat,
} from "../../../constants/constants";
import { useGetEmployeeDetailsQuery } from "../../../features/employee/employeeApi";
import {
  useBariKoiApiHitMutation,
  useGetEmployeeTrackingHistoryQuery,
} from "../../../features/employeeTracking/employeeTrackingApi";
import useError from "../../../hooks/useError";
import { LocationType } from "../../../types/tracking";
import { parseJSON } from "../../../utils/string";

type EmployeeMapAndDetailsProps = {
  date?: string;
};

export default function EmployeeMapAndDetails({
  date,
}: EmployeeMapAndDetailsProps) {
  const [isZoomMap, setIsZoomMap] = useState(true);
  const { viewId } = useParams();
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);
  const [currentCoordinate, setCurrentCoordinate] = useState<
    [number, number] | undefined
  >(undefined);
  const {
    isLoading: isEmployeeDetailsLoading,
    isError: isEmployeeDetailsError,
    error: employeeDetailsError,
    data: employeeDetailsData,
  } = useGetEmployeeDetailsQuery(viewId || "", {
    skip: !viewId,
  });
  const { isLoading, isError, error, data, isFetching } =
    useGetEmployeeTrackingHistoryQuery(
      {
        params: {
          id: viewId || "",
          date: date || "",
        },
      },
      {
        skip: !viewId && !date,
      }
    );
  const [bariKoiApiHit] = useBariKoiApiHitMutation();
  useError(
    isEmployeeDetailsError,
    employeeDetailsError,
    "Get employee data fail"
  );
  useError(isError, error, "Get employee history data fail");

  const employeeInfo = employeeDetailsData?.data?.employee;

  const timelineData = data?.data?.map((el) => {
    const parseJson =
      typeof el?.address === "string"
        ? (parseJSON(el?.address) as LocationType)
        : el?.address;

    return {
      ...el,
      address: {
        address: parseJson?.address || "",
        lng: parseJson?.lng || 0,
        lat: parseJson?.lat || 0,
      },
      time: `${moment(el?.time, dbTimeFormat).format(timeFormat)} - ${moment(
        el?.date,
        dbDateFormat
      ).format(dateFormat)}`,
    };
  });

  const polygonCoordinates: {
    geo: [number, number];
    address: string;
  }[] = useMemo(
    () =>
      timelineData?.map((point) => ({
        geo: [point?.address?.lng as number, point?.address?.lat as number],
        address: point?.address?.address,
      })) || [],
    [timelineData]
  );

  setConfig({ apiKey: BARIKOI_API_KEY, version: "v2" });

  const mapContainer = useRef(null) as any;
  const map = useRef(null) as any;
  const markersRef = useRef<{ [key: string]: any }>({});

  const handleHover = useMemo(
    () =>
      debounce((lng: number, lat: number) => {
        if (map.current) {
          map.current.flyTo({
            center: [lng, lat],
            speed: 2,
          });
        }
      }, 10),
    []
  );

  const handleTimelineHover = (lng: number, lat: number, enlarge: boolean) => {
    const markerId = `${lng}-${lat}`;
    const marker = markersRef.current[markerId];

    setHoveredMarker(enlarge ? markerId : null);

    if (marker) {
      if (enlarge) {
        marker.getElement().style.transform = `${
          marker.getElement().style.transform
        } scale(1.5)`;
      } else {
        marker.getElement().style.transform = marker
          .getElement()
          .style.transform?.replace(/scale\([0-9.]+\)/g, "scale(1)");
      }
    }
  };

  const cleanupMap = () => {
    if (map.current) {
      map.current = null;
    }
  };

  // console.log(polygonCoordinates);

  useEffect(() => {
    if (map.current && Object.keys(map.current || {}).length) return;

    if (!isLoading && !isFetching) {
      map.current = new Map({
        container: mapContainer?.current as any,
        center: polygonCoordinates?.[0]?.geo || [
          90.39017821904588, 23.719800220780733,
        ],
        zoom: 12,
        doubleClickZoom: false,
        accessToken: BARIKOI_API_KEY,
      });

      // Hit the BariKoi API to register the visit
      bariKoiApiHit();

      map.current.on("style.load", () => {
        map.current.addSource("route", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: polygonCoordinates?.map((item) => item?.geo),
            },
          },
        });

        map.current.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": colors?.tintNeutral,
            "line-width": 2,
          },
        });

        polygonCoordinates?.forEach((item) => {
          const markerId = `${item?.geo[0]}-${item?.geo[1]}`;
          const newMarker = new Marker({
            color: colors?.primary,
            draggable: false,
            scale: 1,
          })
            .setLngLat(item?.geo)
            .addTo(map.current);

          const popup = new Popup({
            focusAfterOpen: false,
            offset: [0, -35],
          })
            .setLngLat(item?.geo)
            .setHTML(`<p class="text-center text-header">${item?.address}</p>`);

          markersRef.current[markerId] = newMarker;

          newMarker.getElement().addEventListener("mouseenter", () => {
            setHoveredMarker(markerId);
            popup.addTo(map.current);
          });

          newMarker.getElement().addEventListener("mouseleave", () => {
            setTimeout(() => {
              setHoveredMarker(null);
              popup.remove();
            }, 100);
          });
        });

        map.current.addControl(new FullscreenControl());
        map.current.addControl(new NavigationControl());
        map.current.addControl(new GeolocateControl());
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCoordinate,
    handleHover,
    hoveredMarker,
    isFetching,
    isLoading,
    polygonCoordinates,
  ]);

  // useEffect(() => {
  //   if (map.current && !isLoading && polygonCoordinates?.length && isZoomMap) {
  //     setTimeout(() => {
  //       map.current.flyTo({
  //         center: polygonCoordinates?.[0]?.geo?.length
  //           ? polygonCoordinates?.[0]?.geo
  //           : [90.39017821904588, 23.719800220780733],
  //         zoom: 15,
  //       });

  //       setIsZoomMap(false);
  //     }, 2000);
  //   }
  // }, [isLoading, isZoomMap, polygonCoordinates]);

  useEffect(() => {
    if (!map.current || isLoading || !polygonCoordinates?.length || !isZoomMap)
      return;

    setTimeout(() => {
      if (map.current) {
        // Ensure map.current is still valid before calling flyTo
        map.current.flyTo({
          center: polygonCoordinates?.[0]?.geo?.length
            ? polygonCoordinates?.[0]?.geo
            : [90.39017821904588, 23.719800220780733],
          zoom: 15,
        });

        setIsZoomMap(false);
      }
    }, 2000);
  }, [isLoading, isZoomMap, polygonCoordinates]);

  useEffect(() => {
    if (date) {
      cleanupMap();
    }
  }, [date]);

  useEffect(() => {
    Object.entries(markersRef.current).forEach(([markerId, marker]) => {
      if (marker) {
        if (markerId === hoveredMarker) {
          marker.getElement().style.transform = `${
            marker.getElement().style.transform
          } scale(1.3)`;
        } else {
          marker.getElement().style.transform = marker
            .getElement()
            .style.transform?.replace(/scale\([0-9.]+\)/g, "scale(1)");
        }
      }
    });
  }, [hoveredMarker]);

  const containerStyles = {
    width: "100%",
    height: "100vh",
    minHeight: "400px",
    overflow: "hidden",
  };

  return (
    <div className="grid md:grid-cols-8 gap-6 mb-6">
      <div className="md:col-span-3">
        <div className="bg-white rounded-md border border-extra-light">
          <div className="p-6">
            <InfoTitle className="mb-6" icon={<UserIcon />}>
              Employee Info
            </InfoTitle>
            {isEmployeeDetailsLoading ? (
              <Loader />
            ) : (
              <EmployeeInfoItem
                img={employeeInfo?.member?.profile?.pro_pic || undefined}
                name={employeeInfo?.member?.profile?.name}
                id={employeeInfo?.employee_id}
                designation={employeeInfo?.role?.name}
                department={employeeInfo?.role?.department?.name}
                email={employeeInfo?.member?.profile?.email}
              />
            )}
          </div>

          <div className="mt-4">
            <h4 className="text-header text-xl font-semibold pb-6 px-6">
              Timeline
            </h4>
            <ul className="max-h-[500px] overflow-y-auto">
              {isLoading || isFetching ? (
                <li className="h-40">
                  <Loader />
                </li>
              ) : (
                timelineData?.map((item, index) => (
                  <TimelineItem
                    length={timelineData?.length}
                    index={index}
                    key={index}
                    time={item?.time}
                    details={item?.address?.address}
                    color={index === 0 ? "#1b4dff" : "#FF3838"}
                    onMouseEnter={() => {
                      setCurrentCoordinate([
                        item?.address?.lng as number,
                        item?.address?.lat as number,
                      ]);
                      handleHover(
                        item?.address?.lng as number,
                        item?.address?.lat as number
                      );
                      handleTimelineHover(
                        item?.address?.lng as number,
                        item?.address?.lat as number,
                        true
                      );
                    }}
                    onMouseLeave={() => {
                      handleTimelineHover(
                        item?.address?.lng as number,
                        item?.address?.lat as number,
                        false
                      );
                    }}
                  />
                ))
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="md:col-span-5">
        {isFetching || isLoading ? (
          <div className="mb-2">
            <Loader />
          </div>
        ) : null}
        <div className="rounded-md">
          <div ref={mapContainer} style={containerStyles} />
        </div>
      </div>
    </div>
  );
}
